import React, { useState, useCallback, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Form,
  Button,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { postData, uploadFile } from "../../services/apiServices";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
// Image Cropper Modal Component
const ImageCropperModal = ({ isOpen, toggle, image, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // When modal opens, reset the crop state
  useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setPreviewUrl(null);
    }
  }, [isOpen]);

  // Handle crop complete
  const onCropCompleteCallback = useCallback(
    (_, croppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
      generatePreview(croppedAreaPixels);
    },
    [image]
  );

  // Generate a preview from the crop selection
  const generatePreview = useCallback(
    async (croppedAreaPixels) => {
      if (!croppedAreaPixels || !image) return;

      try {
        const canvas = document.createElement("canvas");
        const img = new Image();
        img.src = image;

        await new Promise((resolve) => {
          img.onload = resolve;
        });

        // Get image dimensions
        const scaleX = img.naturalWidth / img.width;
        const scaleY = img.naturalHeight / img.height;

        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        const ctx = canvas.getContext("2d");

        ctx.drawImage(
          img,
          croppedAreaPixels.x * scaleX,
          croppedAreaPixels.y * scaleY,
          croppedAreaPixels.width * scaleX,
          croppedAreaPixels.height * scaleY,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        const previewUrl = canvas.toDataURL("image/jpeg");
        setPreviewUrl(previewUrl);
      } catch (error) {
        console.error("Error generating preview:", error);
      }
    },
    [image]
  );

  // Create a crop preview and convert to blob for saving
  const createCropPreview = async () => {
    try {
      if (!croppedAreaPixels || !image) return null;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size to exactly 128x128px
      canvas.width = 92;
      canvas.height = 92;

      const imageElement = new Image();
      imageElement.crossOrigin = "anonymous";

      return new Promise((resolve, reject) => {
        imageElement.onload = () => {
          // Draw the cropped area to the canvas and resize to 128x128
          ctx.drawImage(
            imageElement,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            92, // Target width
            92 // Target height
          );

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Failed to create blob"));
              }
            },
            "image/jpeg",
            0.9
          );
        };

        imageElement.onerror = () => {
          reject(new Error("Failed to load image"));
        };

        imageElement.src = image;
      });
    } catch (error) {
      console.error("Error creating crop preview:", error);
      throw error;
    }
  };

  const handleSaveCrop = async () => {
    setLoading(true);
    try {
      const croppedBlob = await createCropPreview();
      if (croppedBlob) {
        onCropComplete(croppedBlob);
        toggle();
      }
    } catch (error) {
      console.error("Error saving crop:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Crop Image (Final size: 128x128px)
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="8">
            <div
              className="crop-container"
              style={{ height: "400px", position: "relative" }}
            >
              {image ? (
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropCompleteCallback}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  showGrid={true}
                />
              ) : (
                <div className="text-center p-4 border rounded">
                  <p>No image loaded. Please select an image first.</p>
                </div>
              )}
            </div>
            <div className="controls mt-3">
              <div className="d-flex align-items-center">
                <span className="me-2">Zoom</span>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="form-range"
                />
              </div>
              <div className="text-muted mt-2">
                {image
                  ? "Use the slider to zoom, drag to reposition, then click Apply Crop"
                  : "Image will appear here for cropping once selected."}
              </div>
            </div>
          </Col>
          <Col md="4">
            <div className="preview-container mt-3">
              <h6>Preview:</h6>
              {previewUrl ? (
                <div
                  className="square-preview"
                  style={{
                    width: "150px",
                    height: "150px",
                    overflow: "hidden",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt="Crop preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: "150px",
                    height: "150px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span className="text-muted">No preview</span>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSaveCrop}
          disabled={loading || !croppedAreaPixels}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Processing...
            </>
          ) : (
            "Apply Crop"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const DraggableImageUpload = ({
  onFileSelect,
  currentImage,
  isUploading,
  uploadError,
  fieldId,
}) => {
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImage || null);

  useEffect(() => {
    if (currentImage) {
      setPreviewUrl(currentImage);
    }
  }, [currentImage]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFile(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFile(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    },
    multiple: false,
  });

  const handleCropComplete = (croppedBlob) => {
    if (croppedBlob) {
      const file = new File([croppedBlob], "cropped-image.jpeg", {
        type: "image/jpeg",
      });
      setPreviewUrl(URL.createObjectURL(croppedBlob));
      onFileSelect(file);
    }
    setShowCropper(false);
  };

  const toggleCropper = () => {
    setShowCropper(!showCropper);
  };

  return (
    <div>
      <div
        {...getRootProps()}
        className={`dropzone-area ${isDragActive ? "active" : ""}`}
        style={{
          border: "2px dashed #ced4da",
          borderRadius: "5px",
          padding: "20px",
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragActive ? "#f8f9fa" : "transparent",
          marginBottom: "15px",
        }}
      >
        <input {...getInputProps()} id={fieldId} accept="image/*" />
        {previewUrl ? (
          <div className="text-center">
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "200px",
                objectFit: "contain",
                marginBottom: "10px",
              }}
              className="img-thumbnail"
            />
            <p className="text-muted mt-2">
              Drag a new image here or click to replace
            </p>
          </div>
        ) : (
          <div>
            <i
              className="mdi mdi-upload"
              style={{
                fontSize: "2rem",
                color: "#6c757d",
                marginBottom: "10px",
              }}
            ></i>
            <p>Drag and drop an image here, or click to select a file</p>
            <p className="text-muted small">Supported formats: JPG, PNG, GIF</p>
          </div>
        )}
      </div>

      {isUploading && (
        <div className="text-center mt-2">
          <Spinner size="sm" color="primary" /> Uploading...
        </div>
      )}

      {uploadError && <div className="text-danger mt-2">{uploadError}</div>}

      <ImageCropperModal
        isOpen={showCropper}
        toggle={toggleCropper}
        image={selectedFile}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
};

const NewCategory = () => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ visible: false, message: "", type: "" });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: "",
      image: "",
      description: "",
    },

    validationSchema: Yup.object().shape({
      name: Yup.string().required("Name is required"),
      image: Yup.string().required("Image is required"),
      description: Yup.string().required("Description is required"),
    }),

    onSubmit: async (values) => {
      try {
        const response = await postData("api/admin/createcategories", {
          name: values.name,
          image: values.image,
          description: values.description,
        });
        if (response.success === true) {
          toast.success(response?.message);
          navigate("/categories");
        }

        validation.resetForm();
        setImagePreview(null);
        setImageFile(null);
      } catch (error) {
        toast.error("Error creating category. Please try again.");
        console.error("Error creating category:", error);
      }
    },
  });

  const handleFileSelect = async (file) => {
    if (file) {
      setImageFile(file);
      setUploadError(null);
      await handleImageUpload(file);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setUploading(true);

    try {
      const fileUrl = await uploadFile(file, "image");

      // Success - update the form with the file URL
      validation.setFieldValue("image", fileUrl);
      setAlert({
        visible: true,
        message: "Image uploaded successfully!",
        type: "success",
      });
      setTimeout(
        () => setAlert({ visible: false, message: "", type: "" }),
        3000
      );
    } catch (error) {
      console.error("Upload error:", error.message);
      setUploadError(`Upload failed: ${error.message}`);
      setAlert({
        visible: true,
        message: `Upload failed: ${error.message}`,
        type: "danger",
      });
      setTimeout(
        () => setAlert({ visible: false, message: "", type: "" }),
        3000
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        {alert.visible && (
          <Alert color={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}
        <div className="d-flex justify-content-end mb-3">
          <Button color="primary" onClick={() => navigate("/categories")}>
            Category List
          </Button>
        </div>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit(e);
          }}
        >
          <Card>
            <CardBody>
              <Row>
                <Col lg="4">
                  <div className="mb-3">
                    <Label htmlFor="name">Category Name</Label>
                    <Input
                      type="text"
                      className="form-control"
                      id="name"
                      placeholder="Category name"
                      value={validation.values.name}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                    {validation.touched.name && validation.errors.name ? (
                      <div className="text-danger">
                        {validation.errors.name}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="4">
                  <div className="mb-3">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      type="textarea"
                      className="form-control"
                      id="description"
                      placeholder="Category description"
                      value={validation.values.description}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                    {validation.touched.description &&
                    validation.errors.description ? (
                      <div className="text-danger">
                        {validation.errors.description}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="image">Category Image</Label>
                    <DraggableImageUpload
                      onFileSelect={handleFileSelect}
                      currentImage={imagePreview}
                      isUploading={uploading}
                      uploadError={uploadError}
                      fieldId="category-image"
                    />

                    <Input
                      type="hidden"
                      id="image"
                      name="image"
                      value={validation.values.image}
                    />

                    {validation.touched.image && validation.errors.image ? (
                      <div className="text-danger">
                        {validation.errors.image}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>

              {/* Submit button */}
              <Row className="mt-3">
                <Col>
                  <Button type="submit" color="primary" disabled={uploading}>
                    Submit
                  </Button>
                  <Button
                    type="button"
                    color="danger"
                    onClick={() => navigate(-1)}
                    className="ms-2" // 👈 adds margin-left (Bootstrap 5)
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </div>
    </React.Fragment>
  );
};

export default NewCategory;
